import { SelectionItem, GroupedItem } from '@utils';
import { create } from 'zustand'
import { useLanguage } from '../context/LanguageContext'


const emotionalContexts: GroupedItem[] = [
  {
    id: 'affection-intimite',
    name: 'emotionalContexts.affection-intimite.name',
    icon: 'affections',
    description: 'emotionalContexts.affection-intimite.description',
    items: [
      { id: 'affection', name: 'emotionalContexts.affection' },
      { id: 'joie-chaleureuse', name: 'emotionalContexts.joie-chaleureuse' },
      { id: 'tendre-complicite', name: 'emotionalContexts.tendre-complicite' },
      { id: 'presence-reconfortante', name: 'emotionalContexts.presence-reconfortante' },
      { id: 'partage-authentique', name: 'emotionalContexts.partage-authentique' },
    ],
  },
  {
    id: 'aspiration-inspiration',
    name: 'emotionalContexts.aspiration-inspiration.name',
    icon: 'aspiration',
    description: 'emotionalContexts.aspiration-inspiration.description',
    items: [
      { id: 'aspiration', name: 'emotionalContexts.aspiration' },
      { id: 'motivation', name: 'emotionalContexts.motivation' },
      { id: 'source', name: 'emotionalContexts.source' },
      { id: 'elan-creatif', name: 'emotionalContexts.elan-creatif' },
      { id: 'grandeur-ame', name: 'emotionalContexts.grandeur-ame' },
    ],
  },
  {
    id: 'stimulation-aventure',
    name: 'emotionalContexts.stimulation-aventure.name',
    icon: 'stimulation',
    description: 'emotionalContexts.stimulation-aventure.description',
    items: [
      { id: 'stimulation', name: 'emotionalContexts.stimulation' },
      { id: 'aventure', name: 'emotionalContexts.aventure' },
      { id: 'exploration', name: 'emotionalContexts.exploration' },
      { id: 'adrenaline-partagee', name: 'emotionalContexts.adrenaline-partagee' },
      { id: 'fun', name: 'emotionalContexts.fun' },
    ],
  },
  {
    id: 'soutien-stabilite',
    name: 'emotionalContexts.soutien-stabilite.name',
    icon: 'soutien',
    description: 'emotionalContexts.soutien-stabilite.description',
    items: [
      { id: 'soutien', name: 'emotionalContexts.soutien' },
      { id: 'encouragement', name: 'emotionalContexts.encouragement' },
      { id: 'securite', name: 'emotionalContexts.securite' },
      { id: 'apaisement', name: 'emotionalContexts.apaisement' },
      { id: 'croissance-lente', name: 'emotionalContexts.croissance-lente' },
    ],
  },
  {
    id: 'reflexion-profondeur',
    name: 'emotionalContexts.reflexion-profondeur.name',
    icon: 'reflexion',
    description: 'emotionalContexts.reflexion-profondeur.description',
    items: [
      { id: 'apprentissage', name: 'emotionalContexts.apprentissage' },
      { id: 'comprehension', name: 'emotionalContexts.comprehension' },
      { id: 'miroir', name: 'emotionalContexts.miroir' },
      { id: 'conscience-soi', name: 'emotionalContexts.conscience-soi' },
      { id: 'conversations-vraies', name: 'emotionalContexts.conversations-vraies' },
    ],
  },
  {
    id: 'lacher-prise-detente',
    name: 'emotionalContexts.lacher-prise-detente.name',
    icon: 'lacherPrise',
    description: 'emotionalContexts.lacher-prise-detente.description',
    items: [
      { id: 'detente', name: 'emotionalContexts.detente' },
      { id: 'legerete', name: 'emotionalContexts.legerete' },
      { id: 'presence-sans-enjeu', name: 'emotionalContexts.presence-sans-enjeu' },
      { id: 'rire', name: 'emotionalContexts.rire' },
      { id: 'instantaneite', name: 'emotionalContexts.instantaneite' },
    ],
  },
];

interface ContextStore {
  contexts: GroupedItem[]
  getAllContexts: () => SelectionItem[]
  getContextsByGroup: (groupId: string) => SelectionItem[]
}

export const useEmotionalContextStore = create<ContextStore>((set, get) => ({
  contexts: emotionalContexts,
  getAllContexts: () => {
    // Flatten all items and remove duplicates by id
    const all = get().contexts.flatMap(group => group.items)
    const unique: { [id: string]: SelectionItem } = {}
    all.forEach(item => { unique[item.id] = item })
    return Object.values(unique)
  },
  getContextsByGroup: (groupId) => {
    const group = get().contexts.find(g => g.id === groupId)
    return group ? group.items : []
  },
}))

export const useTranslatedEmotionalContextStore = () => {
  const { t } = useLanguage()
  const store = useEmotionalContextStore()

  const translateGroup = (group: GroupedItem): GroupedItem => ({
    ...group,
    name: t(group.name),
    description: group.description ? t(group.description) : undefined,
    items: group.items.map(item => ({ ...item, name: t(item.name) })),
  })

  return {
    contexts: store.contexts.map(translateGroup),
    getAllContexts: () => store.getAllContexts().map(item => ({ ...item, name: t(item.name) })),
    getContextsByGroup: (groupId: string) => store.getContextsByGroup(groupId).map(item => ({ ...item, name: t(item.name) })),
  }
}